import axios from "../config/connections"
import { sendCaptureExceptionToSentry } from "../utils/sentry"
import config from "../config"
import { IFeature, IGeometry } from "interfaces"
import { IGraphFeatureOptions } from "providers/venue/modules/shortestPath/type"

interface IFavIcon {
  size: string
  src: string
  type: string
}

interface IMenuConfig {
  to: string
  label: string
  order?: number
  icon_comp?: string
  icon_path?: string
  image?: string
  hidden?: boolean
}

export interface IMapExtrudeConfig {
  height: number
  category: string
}

export interface IExtrudeConfig {
  kiosk: IMapExtrudeConfig[]
  unit: IMapExtrudeConfig[]
}
interface IMapLightConfig {
  color: string
  position?: number[]
  intensity: number
}

interface ILightConfig {
  ambientLight: IMapLightConfig
  directionalLight: IMapLightConfig
}

interface IShortestPathConfig {
  resolution: number
}

type AmenityHighlightMode = "2DMarker"

export interface IAllVenuesViewConfig {
  zoom?: number
  pitch?: number
  center?: Coordinate
  bearing?: number
}

export interface IMapAnimation {
  [k: string]: {
    enable?: boolean
    speed?: number
  }
}
export interface IBasemapConfig {
  // Set to `false` to render no basemap tile layer at all.
  enabled?: boolean
  url_template?: string
  subdomains?: string[]
  opacity?: number
  // CSS filter applied to the tile layer only (e.g. "grayscale(1)"); leave
  // unset/empty for providers whose tiles are already styled.
  css_filter?: string
  max_available_zoom?: number
}

interface IMapConfig {
  amenity_highlight_mode?: AmenityHighlightMode
  default_zoom?: number
  max_zoom?: number
  min_zoom?: number
  kiosk_pixel_ratio?: number
  mobile_pixel_ratio?: number
  extrude?: IExtrudeConfig
  light?: ILightConfig
  shortest_path?: IShortestPathConfig
  basemap?: IBasemapConfig
  text_marker_type?: "marker" | "ui-marker"
  // Add space around highlighted features with `extent_features_scale_factor` to prevent markers from going out of frame.
  extent_features_scale_factor?: number
  // Scale factor to expand the area limit defined by setMaxExtent on the map.
  extent_scale_map_view_factor?: number
  all_venues_view?: IAllVenuesViewConfig
  animations?: IMapAnimation[]
  prepare_steps?: IGraphFeatureOptions
}

interface ISponsorContentConfig {
  disable?: boolean
  limit?: number
  frequency?: number
}

interface IDeviceConfig {
  navigation: {
    main: IMenuConfig[]
    menus: IMenuConfig[]
  }
  search: {
    featureTypes: string[]
    key: {
      name: string
      weight: number
    }
  }
  autoplay?: {
    step: {
      delay: number
      enable: boolean
    }
  }
  sponsor: {
    [k: string]: ISponsorContentConfig
  }
}

interface IAppConfig {
  homeNavigations: IMenuConfig[]
  kiosk: IDeviceConfig
  kioskNavigations: IMenuConfig[]
  map: IMapConfig
  mobile: IDeviceConfig
}

interface IAppManifest {
  icons: IFavIcon[]
  name: string
  theme_color?: string
}

interface IGeojson {
  type: string
  features: IFeature[]
}

type Coordinate = [number, number]
type LinearRingCoordinates = Coordinate[]
type PolygonCoordinates = LinearRingCoordinates[]
type MultiPolygonCoordinates = PolygonCoordinates[]

interface IMapLabel {
  name: string
  geometry: {
    coordinates: PolygonCoordinates | MultiPolygonCoordinates
  }
  properties: Record<string, any>
}

interface IMapDecoration {
  name: string
  geometry: IGeometry
}

type FetchGeojson = (type: string) => Promise<{
  type: string
  data: IFeature[]
}>

const get = async <T>(endpoint: string): Promise<T> => {
  try {
    const res = await axios.get(endpoint)
    if (res.status !== 200) {
      throw new Error()
    }
    return res.data as T
  } catch (err) {
    throw err.message
  }
}

export const fetchAppManifest = async () => {
  try {
    const path = `/manifest.json`
    const jsonRes = await get<IAppManifest>(path)
    return jsonRes
  } catch (error) {
    sendCaptureExceptionToSentry({
      error,
      level: "error",
      transactionName: `Failed to fetch data from /manifest.json endpoint.`,
    })

    // Return empty object if error
    return {}
  }
}

export const fetchSponsoredContent = async () => {
  try {
    const path = `/sponsored-content.json`
    const jsonRes = await get<IAppManifest>(path)
    return jsonRes
  } catch (error) {
    sendCaptureExceptionToSentry({
      error,
      level: "error",
      transactionName: `Failed to fetch data from /sponsored-content.json endpoint.`,
    })

    // Return empty object if error
    return {}
  }
}

export const fetchGeojson: FetchGeojson = async (type) => {
  try {
    const path = `/imdf/${type}.geojson`
    const jsonRes = await get<IGeojson>(path)
    return { type, data: jsonRes.features }
  } catch (error) {
    sendCaptureExceptionToSentry({
      error,
      level: "error",
      transactionName: `Failed to fetch data from /imdf/${type}.geojson endpoint.`,
    })

    // Return empty array if error
    return { type, data: [] }
  }
}

export const fetchMapLabels = async () => {
  const path = `/labels.geojson`
  try {
    const jsonRes = await get<IMapLabel[]>(path)
    return jsonRes
  } catch (error) {
    sendCaptureExceptionToSentry({
      error,
      level: "error",
      transactionName: `Failed to fetch data from ${path} endpoint.`,
    })
    // Return empty array if error
    return { data: [] }
  }
}

export const fetchAppConfig = async () => {
  try {
    const path = `${config("host")}/delivery/config/${config("appConfigId")}`
    const jsonRes = await get<IAppConfig>(path)
    return jsonRes
  } catch (error) {
    sendCaptureExceptionToSentry({
      error,
      level: "error",
      transactionName: `Failed to fetch App config endpoint.`,
    })

    // Return null if error
    return null
  }
}

export const fetchMapDecorations = async () => {
  const path = `/elements`
  try {
    const jsonResponse = await get<IMapDecoration[]>(path)
    return jsonResponse
  } catch (error) {
    sendCaptureExceptionToSentry({
      error,
      level: "error",
      transactionName: `Failed to fetch data from ${path} endpoint.`,
    })
    return { data: [] }
  }
}
